import { fetchJson } from './util'
import { NcCachedDivision } from './ncCache'

const API_BASE = 'https://api-netball.squadi.com'

// --- Organisations (public) ---

export async function fetchOrganisations(): Promise<NetballConnectOrganizations> {
	return fetchJson<NetballConnectOrganizations>(`${API_BASE}/users/api/organisations/all`)
}

export async function fetchDivisions(competitionKey: string): Promise<NcDivisionResponse> {
	return fetchJson<NcDivisionResponse>(`${API_BASE}/livescores/division?competitionKey=${competitionKey}`)
}

// --- Division response shape ---

export type NcDivision = {
	id: number
	name: string
	divisionName?: string
	competitionId: number
	competition?: {
		id: number
		uniqueKey?: string
		name: string
		yearRefId?: number
	}
}

type NcDivisionResponse = NcDivision[]

// --- Ladder response shape ---

type NcLadderTeam = {
	id: number
	teamUniqueKey: string
	logoUrl: string
	name: string
	divisionId: number
	divisionName: string
}

type NcLadderResponse = {
	ladders: NcLadderTeam[]
}

// --- Flat team entry for the picker ---

export type FlatTeamEntry = {
	teamId: number
	teamName: string
	logoUrl?: string
	divId: number
	divName: string
	competitionId: number
	compName: string
	competitionKey: string
}

type LatestTeamEntry = FlatTeamEntry & {
	latestMatchTime: number
}

function withOptionalLogoUrl(logoUrl?: string | null) {
	return logoUrl ? { logoUrl } : {}
}

export function isSelectableNetballConnectTeamName(teamName: string | null | undefined) {
	const normalized = String(teamName ?? '').trim().toLowerCase()
	return normalized.length > 0 && normalized !== 'bye'
}

// --- Core public fetcher ---

export async function fetchDivisionsAndTeams(
	competitionKey: string,
	competitionId: number,
	compName: string,
	divisionList?: NcDivisionResponse,
): Promise<{ divisions: Record<string, NcCachedDivision>; teams: FlatTeamEntry[] }> {

	// 1. Get divisions (public)
	const divisionsResponse = divisionList ?? await fetchDivisions(competitionKey)

	// 2. Fetch all matches for the competition (public, no teamIds filter)
	const allMatches = await fetchJson<NetballConnectMatches>(
		`${API_BASE}/livescores/round/matches?competitionId=${competitionId}&teamIds=&ignoreStatuses=[1]`,
	)

	// Build division map from the division endpoint
	const divisions: Record<string, NcCachedDivision> = {}
	for (const div of divisionsResponse) {
		divisions[String(div.id)] = {
			divName: div.name || div.divisionName || `Division ${div.id}`,
			teams: {},
		}
	}

	// Extract teams from match data. If grading changes move a team between
	// divisions, keep the division from that team's latest visible fixture.
	const latestTeamEntries = new Map<number, LatestTeamEntry>()

	for (const round of allMatches.rounds || []) {
		for (const match of round.matches || []) {
			const divId = match.divisionId || round.divisionId
			const divKey = String(divId)

			for (const side of ['team1', 'team2'] as const) {
				const team = match[side]
				const teamId = match[`${side}Id`]
				if (!team || !teamId || !isSelectableNetballConnectTeamName(team.name)) continue

				if (!divisions[divKey]) {
					divisions[divKey] = {
						divName: round.division?.name || `Division ${divId}`,
						teams: {},
					}
				}

				const latestMatchTime = (new Date(match.startTime)).getTime() || 0
				const existing = latestTeamEntries.get(teamId)
				if (existing && existing.latestMatchTime > latestMatchTime) continue

				latestTeamEntries.set(teamId, {
					teamId,
					teamName: team.name,
					...withOptionalLogoUrl(team.logoUrl),
					divId,
					divName: divisions[divKey].divName,
					competitionId,
					compName,
					competitionKey,
					latestMatchTime,
				})
			}
		}
	}

	const teams: FlatTeamEntry[] = [...latestTeamEntries.values()].map(entry => {
		const { latestMatchTime, ...teamEntry } = entry
		divisions[String(teamEntry.divId)].teams[String(teamEntry.teamId)] = {
			teamName: teamEntry.teamName,
			...withOptionalLogoUrl(teamEntry.logoUrl),
		}
		return teamEntry
	})

	// 3. Fallback: if matches are sparse, try ladder per division
	if (teams.length === 0) {
		const seenTeamIds = new Set<number>()
		for (const div of divisionsResponse) {
			try {
				const ladder = await fetchJson<NcLadderResponse>(
					`${API_BASE}/livescores/teams/ladder/v2?divisionIds=${div.id}&competitionKey=${competitionKey}&filteredOutCompStatuses=1&sportRefId=1`,
				)
				const divKey = String(div.id)
				for (const entry of ladder.ladders || []) {
					if (seenTeamIds.has(entry.id) || !isSelectableNetballConnectTeamName(entry.name)) continue
					seenTeamIds.add(entry.id)

					if (!divisions[divKey]) {
						divisions[divKey] = {
							divName: div.name || div.divisionName || `Division ${div.id}`,
							teams: {},
						}
					}
					divisions[divKey].teams[String(entry.id)] = {
						teamName: entry.name,
						...withOptionalLogoUrl(entry.logoUrl),
					}

					teams.push({
						teamId: entry.id,
						teamName: entry.name,
						...withOptionalLogoUrl(entry.logoUrl),
						divId: div.id,
						divName: entry.divisionName || div.name || `Division ${div.id}`,
						competitionId,
						compName,
						competitionKey,
					})
				}
			} catch (err) {
				console.warn(`Ladder fetch failed for division ${div.id}:`, err)
			}
		}
	}

	return { divisions, teams }
}
